import { withGitpodSize } from "@/assets/icons/utils";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { ErrorFallback } from "@/components/ErrorFallback";
import { Button } from "@/components/flexkit/Button";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { useListPrompts, type PlainPrompt } from "@/queries/prompt-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import IconPlusSmall from "central-icons/IconPlusSmall";
import { FileDown } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { AgentCommandCard } from "@/routes/settings/organization/agents/AgentCommandCard";
import { NewCommandModal } from "@/routes/settings/organization/agents/NewCommandModal";
import { ImportDefaultCommandsDialog } from "@/routes/settings/organization/agents/ImportDefaultCommandsDialog";

export const AgentCommands: FC = () => {
    const { data: user, isPending: isUserPending, error: userError } = useAuthenticatedUser();
    const { data: commands, isPending, error } = useListPrompts({ isCommand: true });

    const [showNewCommandModal, setShowNewCommandModal] = useState(false);
    const [showImportDefaultsModal, setShowImportDefaultsModal] = useState(false);

    const onNewCommand = useCallback(() => {
        setShowNewCommandModal(true);
    }, []);

    const onImportDefaults = useCallback(() => {
        setShowImportDefaultsModal(true);
    }, []);

    const content = useMemo(() => {
        if (error || userError) {
            return <ErrorFallback error={error || userError!} />;
        }

        if (isPending || isUserPending || !user) {
            return (
                <SkeletonBlock
                    ready={!isPending && !isUserPending}
                    failed={!!error || !!userError}
                    className="h-[160px] w-full"
                />
            );
        }

        return (
            <AgentCommandsContent
                commands={commands?.prompts ?? []}
                onNewCommand={onNewCommand}
                onImportDefaults={onImportDefaults}
            />
        );
    }, [error, userError, isPending, isUserPending, user, commands, onNewCommand, onImportDefaults]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Heading3>Commands</Heading3>
            </div>
            <div data-testid="agent-commands-section">
                {content}
                <NewCommandModal open={showNewCommandModal} onClose={() => setShowNewCommandModal(false)} />
                <ImportDefaultCommandsDialog
                    open={showImportDefaultsModal}
                    onClose={() => setShowImportDefaultsModal(false)}
                />
            </div>
        </div>
    );
};

const AgentCommandsContent: FC<{
    commands: PlainPrompt[];
    onNewCommand: () => void;
    onImportDefaults: () => void;
}> = ({ commands, onNewCommand, onImportDefaults }) => {
    if (commands.length === 0) {
        return (
            <EmptyStateCard
                title="No agent commands"
                description="Create custom commands that can be used across your organization to streamline common AI tasks."
                data-testid="agent-commands-empty-state"
            >
                <div className="flex gap-2">
                    <Button
                        onClick={onImportDefaults}
                        data-testid="import-defaults-empty-state-button"
                        data-tracking-id="import-default-commands-empty-state"
                    >
                        Import Defaults
                    </Button>
                    <Button
                        onClick={onNewCommand}
                        variant="secondary"
                        data-testid="add-command-modal-trigger"
                        data-tracking-id="add-agent-command-agent-commands-content"
                    >
                        New Command
                    </Button>
                </div>
            </EmptyStateCard>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4">
                <Text className="text-base text-content-secondary">
                    These are organization-wide commands that can be used by any member to interact with AI agents.
                </Text>
            </div>
            <CommandGrid commands={commands} onNewCommand={onNewCommand} onImportDefaults={onImportDefaults} />
        </div>
    );
};

const CommandGrid: FC<{
    commands: PlainPrompt[];
    onNewCommand: () => void;
    onImportDefaults: () => void;
}> = ({ commands, onNewCommand, onImportDefaults }) => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredCommands = useMemo(() => {
        if (!searchQuery.trim()) return commands;
        const query = searchQuery.toLowerCase().replace(/^\//, "");
        return commands.filter(
            (command) =>
                command.metadata?.name?.toLowerCase().includes(query) ||
                command.metadata?.description?.toLowerCase().includes(query) ||
                command.spec?.command?.toLowerCase().includes(query),
        );
    }, [commands, searchQuery]);

    const showSearch = commands.length > 1;

    return (
        <>
            <div className="flex items-center justify-between gap-4">
                {showSearch ? (
                    <SearchInput
                        wrapperClassName="flex-1"
                        className="max-w-none"
                        placeholder="Search commands by name, description, or command"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery("")}
                        data-testid="command-search-input"
                    />
                ) : (
                    <div className="flex-1" />
                )}
                <ButtonGroup variant="primary">
                    <ButtonGroupItem
                        LeadingIcon={withGitpodSize(IconPlusSmall)}
                        onClick={onNewCommand}
                        data-testid="add-command-button-inline"
                        data-tracking-id="add-agent-command-agent-commands-content-inline"
                    >
                        New Command
                    </ButtonGroupItem>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ButtonGroupItem
                                LeadingIcon={IconChevronDownSmall}
                                aria-label="More command options"
                                data-testid="add-command-dropdown-trigger"
                            />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={onImportDefaults}
                                data-testid="import-defaults-menu-item"
                                data-tracking-id="import-default-commands-menu"
                            >
                                <FileDown className="mr-2 size-4" />
                                Import Defaults
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </ButtonGroup>
            </div>
            {filteredCommands.length === 0 ? (
                <EmptyStateCard
                    title="No matching commands"
                    description="No commands match your search. Try a different search term."
                    data-testid="no-matching-commands-empty-state"
                />
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    {filteredCommands.map((command) => (
                        <AgentCommandCard key={command.id} command={command} />
                    ))}
                </div>
            )}
        </>
    );
};
